import { useWindowVirtualizer } from "@tanstack/react-virtual"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { HEADER_HEIGHT_VARIABLE_NAME } from "shared/constants/app-constants"
import { useMainRef } from "shared/context/MainRefContext"
import { useResponsiveColumns } from "shared/lib/breakpoints/useResponsiveColumns"
import { useElementHeightStore } from "shared/lib/css/useElementHeightStore"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndexGrid } from "shared/lib/useRovingTabIndexGrid"
import { useScreenSize } from "shared/lib/useScreenSize"
import { useValueRef } from "shared/lib/useValueRef"
import { PROJECTS_OVERVIEW_COLUMNS_BY_BREAKPOINT } from "shared/types/Breakpoints"
import { Project } from "shared/types/Project"
import { OnFocusedItemChange } from "shared/types/RovingTabIndex"

import {
  ProjectsOverviewCardConnector,
  ProjectsOverviewCardConnectorProps
} from "widgets/projects-overview-card/ProjectsOverviewCardConnector"

import { useProjectsOverviewPageAccessibility } from "./hooks/useProjectsOverviewPageAccessibility"

const NUMBER_OF_SKELETON_PROJECTS = 10

// Create skeleton projects with stable IDs for loading state
const SKELETON_PROJECTS: Pick<Project, "id">[] = Array.from(
  { length: NUMBER_OF_SKELETON_PROJECTS },
  (_, i) => ({
    id: `skeleton-${i}`
  })
)

export interface ProjectsOverviewGridProps {
  projects: Project[] | null | undefined
  isLoading?: boolean
  ProjectsCardRenderer?: React.ComponentType<ProjectsOverviewCardConnectorProps>
}

export const ProjectsOverviewGrid = React.memo(
  ({
    projects,
    isLoading,
    ProjectsCardRenderer = ProjectsOverviewCardConnector
  }: ProjectsOverviewGridProps) => {
    const { t } = useTranslation()
    const { isMobile } = useScreenSize()
    const headerHeight = useElementHeightStore(HEADER_HEIGHT_VARIABLE_NAME)

    const [parentElement, setParentElement] = useState<HTMLDivElement | null>(
      null
    )

    const scrollMargin = parentElement?.offsetTop ?? 0

    const fallback = useValueRef(parentElement)
    const mainElementRef = useMainRef({ fallback })

    const columnCount = useResponsiveColumns(mainElementRef.current, {
      columnsByBreakpoint: PROJECTS_OVERVIEW_COLUMNS_BY_BREAKPOINT
    })

    // Use skeleton projects when loading, otherwise use actual projects
    const displayProjects = useMemo(
      () => (isLoading ? SKELETON_PROJECTS : projects),
      [isLoading, projects]
    )

    const rows = useMemo(() => {
      if (!displayProjects || displayProjects.length === 0) return []
      const result = []
      for (let i = 0; i < displayProjects.length; i += columnCount) {
        result.push(displayProjects.slice(i, i + columnCount))
      }
      return result
    }, [displayProjects, columnCount])

    const rowVirtualizer = useWindowVirtualizer({
      count: rows.length,
      gap: 24,
      estimateSize: () => (isMobile ? 300 : 150),
      scrollMargin,
      overscan: 2
    })

    useEffect(() => {
      rowVirtualizer.measure()
    }, [projects, rowVirtualizer])

    const handleElementFocus: OnFocusedItemChange = useEvent(({ index }) => {
      const [targetOffset] = rowVirtualizer.getOffsetForIndex(
        Math.floor(index / columnCount)
      ) || [0]

      const currentOffset = rowVirtualizer.scrollOffset ?? 0
      if (targetOffset < currentOffset) {
        rowVirtualizer.scrollToOffset(targetOffset - headerHeight)
      } else {
        rowVirtualizer.scrollToOffset(targetOffset)
      }
    })

    const {
      containsFocus,
      focusedIndex,
      rowCount,
      getElementProps,
      containerProps
    } = useRovingTabIndexGrid<HTMLButtonElement>({
      elementCount: projects?.length || 0,
      columnsByBreakpoint: PROJECTS_OVERVIEW_COLUMNS_BY_BREAKPOINT,
      disableRovingTabIndex: true,
      isRoundRobin: false,
      onFocusedElementChange: handleElementFocus
    })

    useProjectsOverviewPageAccessibility({
      containsFocus,
      focusedIndex,
      projectCount: displayProjects?.length || 0,
      rowCount,
      columnCount
    })

    if (!displayProjects || displayProjects.length === 0) {
      return null
    }

    return (
      <div
        ref={setParentElement}
        aria-label={t("Project.Projects")}
        className="projects-overview__project-list projects-overview-grid"
        data-testid="project-list"
        role="region"
        aria-live="polite"
        aria-busy={isLoading ? "true" : undefined}
      >
        <div
          {...containerProps}
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: "relative"
          }}
        >
          {isLoading && (
            <div role="status" className="visually-hidden">
              {t("Project.LoadingProjects")}
            </div>
          )}
          {rowVirtualizer.getVirtualItems().map(virtualRow => (
            <div
              key={virtualRow.index}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                transform: `translateY(${virtualRow.start - scrollMargin}px)`
              }}
              className="projects-overview-grid__row"
            >
              {rows[virtualRow.index]?.map((project, colIndex) => {
                const elementIndex = virtualRow.index * columnCount + colIndex
                return (
                  <div key={project.id} aria-hidden={isLoading}>
                    <ProjectsCardRenderer
                      projectID={isLoading ? null : project.id}
                      isLoading={isLoading}
                      {...getElementProps(elementIndex)}
                    />
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    )
  }
)
